import React from 'react';
import { Container, Stack, Title, Text, Card, Grid, Group, Button, Badge, Table, ActionIcon } from '@mantine/core';
import { IconUsers, IconHome, IconSettings, IconTrash, IconEdit, IconPlus } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();

  // Mock data for admin dashboard
  const stats = [
    { title: 'Total Users', value: '1,247', icon: <IconUsers size={24} />, color: 'blue' },
    { title: 'Properties Listed', value: '342', icon: <IconHome size={24} />, color: 'green' },
    { title: 'Active Rentals', value: '89', icon: <IconHome size={24} />, color: 'orange' },
    { title: 'RV/Camping Spots', value: '156', icon: <IconHome size={24} />, color: 'purple' },
  ];

  const recentUsers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'User', joined: '2024-01-15' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Admin', joined: '2024-01-14' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'User', joined: '2024-01-13' },
  ];

  if (!user) {
    return (
      <Container size="md">
        <Title order={1} ta="center" c="green">Please log in to access the admin panel</Title>
      </Container>
    );
  }

  if (!isAdmin()) {
    return (
      <Container size="md">
        <Title order={1} ta="center" c="red">Access Denied</Title>
        <Text ta="center" c="dimmed">You don't have permission to access the admin panel.</Text>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Title order={1} c="green">Admin Panel</Title>
          <Badge color="red" variant="filled">
            Admin Access
          </Badge>
        </Group>
        
        {/* Stats Cards */}
        <Grid>
          {stats.map((stat, index) => (
            <Grid.Col key={index} span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">{stat.title}</Text>
                    <Text size="xl" fw={700} c={stat.color}>
                      {stat.value}
                    </Text>
                  </div>
                  <div style={{ color: `var(--mantine-color-${stat.color}-6)` }}>
                    {stat.icon}
                  </div>
                </Group>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
        
        {/* Quick Actions */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">Quick Actions</Title>
          <Group gap="md">
            <Button leftSection={<IconPlus size={16} />} color="green">
              Add Property
            </Button>
            <Button leftSection={<IconUsers size={16} />} color="blue" variant="outline">
              Manage Users
            </Button>
            <Button leftSection={<IconSettings size={16} />} color="gray" variant="outline">
              System Settings
            </Button>
          </Group>
        </Card>
        
        {/* Recent Users Table */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={3}>Recent Users</Title>
            <Button size="sm" variant="outline">View All</Button>
          </Group>
          
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Joined</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentUsers.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>{user.name}</Table.Td>
                  <Table.Td>{user.email}</Table.Td>
                  <Table.Td>
                    <Badge 
                      color={user.role === 'Admin' ? 'red' : 'green'}
                      variant="light"
                    >
                      {user.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{new Date(user.joined).toLocaleDateString()}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon color="blue" variant="subtle">
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon color="red" variant="subtle">
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
        
        {/* System Information */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">System Information</Title>
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text c="dimmed">Database Status</Text>
                  <Badge color="green">Connected</Badge>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed">Server Status</Text>
                  <Badge color="green">Online</Badge>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed">Last Backup</Text>
                  <Text>2024-01-15 02:00 AM</Text>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text c="dimmed">Active Sessions</Text>
                  <Text>47</Text>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed">Memory Usage</Text>
                  <Text>2.4 GB / 8 GB</Text>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed">Disk Usage</Text>
                  <Text>156 GB / 500 GB</Text>
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
};

export default AdminPanel;
